import React, { useState } from "react"
import { Plus, CalendarClock, Loader2 } from "lucide-react"
import { Button } from "src/components/ui/button"
import { useToast } from "src/components/ui/toast"
import { FollowUpCard } from "./FollowUpCard"
import { FollowUpFormDrawer } from "./FollowUpFormDrawer"
import { useFollowUps } from "../hooks/useFollowUps"
import { completeFollowUp, deleteFollowUp, updateFollowUp } from "../api/followUpsApi"

// Follow-ups panel for Lead/Deal detail pages.
//   <FollowUpsPanel parentType="leads" parentId={leadId} members={membersList} />
export function FollowUpsPanel({ parentType, parentId, members = [], title = "Follow-ups" }) {
  const { toast } = useToast()
  const { followUps, loading, setFollowUps } = useFollowUps({ parentType, parentId })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  const open = followUps?.filter((f) => f.status === "open") || []
  const done = followUps?.filter((f) => f.status === "completed") || []

  function upsert(saved) {
    setFollowUps((prev) => {
      if (!prev) return [saved]
      const exists = prev.find((f) => f.id === saved.id)
      return exists ? prev.map((f) => (f.id === saved.id ? saved : f)) : [saved, ...prev]
    })
  }

  async function handleComplete(id, outcome) {
    try {
      const updated = await completeFollowUp(id, outcome)
      upsert(updated)
      toast({ title: "Follow-up logged" })
    } catch {
      toast({ variant: "destructive", title: "Could not log follow-up" })
    }
  }

  async function handleReopen(id) {
    try {
      // Reopening simply marks it open again, keeping the existing due date.
      const updated = await updateFollowUp(id, { status: "open" })
      upsert(updated)
    } catch {
      toast({ variant: "destructive", title: "Could not reopen follow-up" })
    }
  }

  async function handleDelete(id) {
    try {
      await deleteFollowUp(id)
      setFollowUps((prev) => prev.filter((f) => f.id !== id))
    } catch {
      toast({ variant: "destructive", title: "Could not delete follow-up" })
    }
  }

  function handleEdit(followUp) {
    setEditing(followUp)
    setDrawerOpen(true)
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <CalendarClock className="h-4 w-4 text-slate-400" /> {title}
          {open.length > 0 && (
            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
              {open.length}
            </span>
          )}
        </span>
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => { setEditing(null); setDrawerOpen(true) }}>
          <Plus className="h-3.5 w-3.5" /> Schedule
        </Button>
      </div>

      {/* Body */}
      <div className="space-y-2 p-3">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
          </div>
        ) : open.length === 0 && done.length === 0 ? (
          <div className="px-2 py-6 text-center">
            <CalendarClock className="mx-auto mb-2 h-7 w-7 text-slate-200" />
            <p className="text-[13px] text-slate-400">No follow-ups scheduled</p>
            <Button
              variant="ghost"
              onClick={() => { setEditing(null); setDrawerOpen(true) }}
              className="mt-2 h-auto p-0 text-[12px] font-medium text-blue-600 hover:bg-transparent hover:text-blue-700"
            >
              Schedule the first one
            </Button>
          </div>
        ) : (
          <>
            {open.map((fu) => (
              <FollowUpCard
                key={fu.id}
                followUp={fu}
                onComplete={handleComplete}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
            {done.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer select-none text-[12px] text-slate-400 hover:text-slate-600">
                  {done.length} completed
                </summary>
                <div className="mt-2 space-y-2">
                  {done.map((fu) => (
                    <FollowUpCard
                      key={fu.id}
                      followUp={fu}
                      onReopen={handleReopen}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>

      <FollowUpFormDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null) }}
        onSaved={upsert}
        followUp={editing}
        parent={{ type: parentType, id: parentId }}
        members={members}
      />
    </div>
  )
}
